import {
  ref,
  computed,
  watch,
} from '@vue/composition-api'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'

const pickVal = v => (v && typeof v === 'object' ? v.value : v)
const pickGroup = v => {
  if (!v) return ''
  return typeof v === 'object' ? (v.value || v.label || '') : v
}

function buildAuthHeaders() {
  const token = (typeof window !== 'undefined' && window.$cookies)
    ? window.$cookies.get('userToken')
    : ''
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

/**
 * Factory: every tab gets its OWN set of refs,
 * its OWN watcher,
 * its OWN fetch function and its OWN totalItems/dataMeta.
 *
 * `staticParams` are the params that identify the tab on the backend,
 * e.g. { is_ongoing_dispose: 'true',
 * is_disposed: 'true' } for Disposed.
 */
function createTab(staticParams = {}) {
  const refTable = ref(null)
  const currentPage = ref(1)
  const perPage = ref(10)
  const totalItems = ref(0)
  const searchQuery = ref('')
  const sortBy = ref('asset_created_time')
  const isSortDirDesc = ref(true)
  const filterBranch = ref(null)
  const filterAssetGroup = ref(null)
  const filterAssetStatus = ref(null)
  const startDateFilter = ref('')
  const endDateFilter = ref('')

  const dataMeta = computed(() => {
    const localItemsCount = refTable.value
      ? (refTable.value.localItems || []).length
      : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItems.value,
    }
  })

  const refetchData = () => {
    if (refTable.value && typeof refTable.value.refresh === 'function') {
      refTable.value.refresh()
    }
  }

  // Reset to page 1 whenever a filter (not page itself) changes
  watch(
    [
      searchQuery,
      filterBranch,
      filterAssetGroup,
      filterAssetStatus,
      startDateFilter,
      endDateFilter,
      perPage,
    ],
    () => {
      if (currentPage.value !== 1) {
        currentPage.value = 1
      } else {
        refetchData()
      }
    },
  )

  // Just re-fetch on page change (no reset)
  watch(currentPage, () => {
    refetchData()
  })

  const fetchAssets = (ctx, callback) => {
    const params = { ...staticParams }
    params.limit = perPage.value
    params.offset = (currentPage.value - 1) * perPage.value
    if (searchQuery.value) params.search = searchQuery.value
    if (sortBy.value) {
      params.order_by = sortBy.value
      params.order_sort = isSortDirDesc.value ? 'DESC' : 'ASC'
    }
    const b = pickVal(filterBranch.value)
    if (b) params.branch_id = b
    const g = pickGroup(filterAssetGroup.value)
    if (g) params.group = g
    const s = pickVal(filterAssetStatus.value)
    if (s === 'old') params.is_old = 'true'
    else if (s === 'new') params.is_old = 'false'
    if (startDateFilter.value) params.date_start = startDateFilter.value
    if (endDateFilter.value) params.date_end = endDateFilter.value

    return axios
      .get(
        `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ASSET}`,
        {
          headers: buildAuthHeaders(),
          params,
        },
      )
      .then(response => {
        const payload = response.data || {}
        const list = payload.data || []

        // Fixed: Replaced nested ternary with Nullish Coalescing (??)
        const total = Number(payload.data_count ?? payload.data_count ?? 0) || 0

        totalItems.value = total
        if (callback) callback(list)
        return list
      })
      .catch(err => {
        console.error('fetchAssets error', err)
        totalItems.value = 0
        if (callback) callback([])
        return []
      })
  }

  return {
    refTable,
    currentPage,
    perPage,
    totalItems,
    searchQuery,
    sortBy,
    isSortDirDesc,
    filterBranch,
    filterAssetGroup,
    filterAssetStatus,
    startDateFilter,
    endDateFilter,
    dataMeta,
    fetchAssets,
    refetchData,
  }
}

export default function useAssetsList() {
  const perPageOptions = [10, 25, 50, 100]

  // Fixed: object-curly-newline rules applied
  const assetStatusOptions = [
    {
      label: 'All',
      value: null,
    },
    {
      label: 'New',
      value: 'new',
    },
    {
      label: 'Old',
      value: 'old',
    },
  ]

  const tableColumns = [
    {
      key: 'AssetID',
      label: 'Asset ID',
      sortable: true,
    },
    {
      key: 'ItemName',
      label: 'Item Name',
    },
    {
      key: 'Origin',
      label: 'Origin',
    },
    {
      key: 'CurrentLocation',
      label: 'Current Location',
    },
    {
      key: 'CurrentCondition',
      label: 'Condition',
    },
    {
      key: 'CurrentStatus',
      label: 'Status',
    },
    {
      key: 'Thumbnail',
      label: 'Photo',
    },
    {
      key: 'DateCreated',
      label: 'Created',
    },
    {
      key: 'DateModified',
      label: 'Modified',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ]

  const tableColumnsUpdated = [
    {
      key: 'AssetID',
      label: 'Asset ID',
      sortable: true,
    },
    {
      key: 'ItemName',
      label: 'Item Name',
    },
    {
      key: 'Origin',
      label: 'Origin',
    },
    {
      key: 'CurrentLocation',
      label: 'Current Location',
    },
    {
      key: 'CurrentCondition',
      label: 'Condition',
    },
    {
      key: 'CurrentStatus',
      label: 'Status',
    },
    {
      key: 'Thumbnail',
      label: 'Photo',
    },
    {
      key: 'DateCreated',
      label: 'Created',
    },
    {
      key: 'DateUpdated',
      label: 'Updated',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ]

  const tableColumnsDisposed = [
    {
      key: 'AssetID',
      label: 'Asset ID',
      sortable: true,
    },
    {
      key: 'ItemName',
      label: 'Item Name',
    },
    {
      key: 'Origin',
      label: 'Origin',
    },
    {
      key: 'CurrentLocation',
      label: 'Current Location',
    },
    {
      key: 'CurrentCondition',
      label: 'Condition',
    },
    {
      key: 'CurrentStatus',
      label: 'Status',
    },
    {
      key: 'Thumbnail',
      label: 'Photo',
    },
    {
      key: 'DateCreated',
      label: 'Created',
    },
    {
      key: 'DateDisposed',
      label: 'Disposed',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ]

  const tableColumnsReturned = [
    {
      key: 'AssetID',
      label: 'Asset ID',
      sortable: true,
    },
    {
      key: 'ItemName',
      label: 'Item Name',
    },
    {
      key: 'Origin',
      label: 'Origin',
    },
    {
      key: 'CurrentLocation',
      label: 'Current Location',
    },
    {
      key: 'CurrentCondition',
      label: 'Condition',
    },
    {
      key: 'CurrentStatus',
      label: 'Status',
    },
    {
      key: 'Thumbnail',
      label: 'Photo',
    },
    {
      key: 'DateCreated',
      label: 'Created',
    },
    {
      key: 'DateReturned',
      label: 'Returned',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ]

  const resolveStatusVariant = status => {
    const s = (status || 'active').toString().toLowerCase()
    if (s === 'active') return 'success'
    if (s === 'disposed') return 'danger'
    if (s === 'inactive' || s === 'pending') return 'secondary'
    return 'primary'
  }

  // Fixed: object-curly-newline for method arguments
  const active = createTab({})

  // --- Active tab: switchable card/table view (default: card) -----------
  const viewMode = ref('card')
  const activeCardItems = ref([])
  const isLoadingActiveCards = ref(false)

  const buildActiveCardParams = () => {
    const params = {}
    params.limit = active.perPage.value
    params.offset = (active.currentPage.value - 1) * active.perPage.value
    if (active.searchQuery.value) params.search = active.searchQuery.value
    params.order_by = 'asset_number'
    params.order_sort = 'ASC'
    const b = pickVal(active.filterBranch.value)
    if (b) params.branch_id = b
    const g = pickGroup(active.filterAssetGroup.value)
    if (g) params.group = g
    const s = pickVal(active.filterAssetStatus.value)
    if (s === 'old') params.is_old = 'true'
    else if (s === 'new') params.is_old = 'false'
    if (active.startDateFilter.value) params.date_start = active.startDateFilter.value
    if (active.endDateFilter.value) params.date_end = active.endDateFilter.value
    return params
  }

  const fetchActiveCardData = () => {
    isLoadingActiveCards.value = true
    return axios
      .get(
        `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ASSET}`,
        {
          headers: buildAuthHeaders(),
          params: buildActiveCardParams(),
        },
      )
      .then(response => {
        const payload = response.data || {}
        const list = payload.data || []
        active.totalItems.value = Number(payload.data_count ?? 0) || 0
        activeCardItems.value = list
      })
      .catch(err => {
        console.error('fetchActiveCardData error', err)
        active.totalItems.value = 0
        activeCardItems.value = []
      })
      .finally(() => {
        isLoadingActiveCards.value = false
      })
  }

  const getActiveLocalCount = () => {
    if (viewMode.value !== 'table') return activeCardItems.value.length
    return active.refTable.value ? (active.refTable.value.localItems || []).length : 0
  }

  const activeDataMeta = computed(() => {
    const count = getActiveLocalCount()
    return {
      from: active.perPage.value * (active.currentPage.value - 1) + (count ? 1 : 0),
      to: active.perPage.value * (active.currentPage.value - 1) + count,
      of: active.totalItems.value,
    }
  })

  const refetchActive = () => {
    if (viewMode.value === 'table') {
      active.refetchData()
    } else {
      fetchActiveCardData()
    }
  }

  // Keep card data in sync with the same filters/paging the table watches.
  watch(
    [
      active.searchQuery,
      active.filterBranch,
      active.filterAssetGroup,
      active.filterAssetStatus,
      active.startDateFilter,
      active.endDateFilter,
      active.perPage,
      active.currentPage,
    ],
    () => {
      if (viewMode.value === 'card') fetchActiveCardData()
    },
  )

  watch(viewMode, () => {
    active.currentPage.value = 1
    if (viewMode.value === 'card') fetchActiveCardData()
  })

  const updateOnGoing = createTab({
    is_ongoing_update: 'true',
  })
  const updateNA = createTab({
    is_ongoing_update: 'true',
    needs_approval_update: 'true',
  })
  const updated = createTab({
    is_ongoing_update: 'true',
    is_updated: 'true',
  })
  const disposeOnGoing = createTab({
    is_ongoing_dispose: 'true',
  })
  const disposeNA = createTab({
    is_ongoing_dispose: 'true',
    needs_approval: 'true',
  })
  const disposed = createTab({
    is_ongoing_dispose: 'true',
    is_disposed: 'true',
  })
  const returnOnGoing = createTab({
    is_ongoing_return: 'true',
  })
  const returnNA = createTab({
    is_ongoing_return: 'true',
    needs_approval_return: 'true',
  })
  const returned = createTab({
    is_ongoing_return: 'true',
    is_returned: 'true',
  })

  const refetchData = () => {
    active.refetchData()
    updateOnGoing.refetchData()
    updateNA.refetchData()
    updated.refetchData()
    disposeOnGoing.refetchData()
    disposeNA.refetchData()
    disposed.refetchData()
    returnOnGoing.refetchData()
    returnNA.refetchData()
    returned.refetchData()
  }

  return {
    // shared
    perPageOptions,
    assetStatusOptions,
    tableColumns,
    tableColumnsUpdated,
    tableColumnsDisposed,
    tableColumnsReturned,
    resolveStatusVariant,
    refetchData,

    // ============ Active ============
    refAssetsListTable: active.refTable,
    currentPage: active.currentPage,
    perPage: active.perPage,
    totalItems: active.totalItems,
    searchQuery: active.searchQuery,
    sortBy: active.sortBy,
    isSortDirDesc: active.isSortDirDesc,
    filterBranch: active.filterBranch,
    filterAssetGroup: active.filterAssetGroup,
    filterAssetStatus: active.filterAssetStatus,
    startDateFilter: active.startDateFilter,
    endDateFilter: active.endDateFilter,
    dataMeta: activeDataMeta,
    fetchAssets: active.fetchAssets,

    // Active tab switchable card/table view
    viewMode,
    activeCardItems,
    isLoadingActiveCards,
    fetchActiveCardData,
    refetchActive,

    // ============ Update -> On Going ============
    refAssetsListTableUpdateOnGoing: updateOnGoing.refTable,
    currentPageUpdateOnGoing: updateOnGoing.currentPage,
    perPageUpdateOnGoing: updateOnGoing.perPage,
    totalItemsUpdateOnGoing: updateOnGoing.totalItems,
    searchQueryUpdateOnGoing: updateOnGoing.searchQuery,
    sortByUpdateOnGoing: updateOnGoing.sortBy,
    isSortDirDescUpdateOnGoing: updateOnGoing.isSortDirDesc,
    filterBranchUpdateOnGoing: updateOnGoing.filterBranch,
    filterAssetGroupUpdateOnGoing: updateOnGoing.filterAssetGroup,
    filterAssetStatusUpdateOnGoing: updateOnGoing.filterAssetStatus,
    startDateFilterUpdateOnGoing: updateOnGoing.startDateFilter,
    endDateFilterUpdateOnGoing: updateOnGoing.endDateFilter,
    dataMetaUpdateOnGoing: updateOnGoing.dataMeta,
    fetchAssetsUpdateOnGoing: updateOnGoing.fetchAssets,

    // ============ Update -> Needs Approval ============
    refAssetsListTableUpdateNA: updateNA.refTable,
    currentPageUpdateNA: updateNA.currentPage,
    perPageUpdateNA: updateNA.perPage,
    totalItemsUpdateNA: updateNA.totalItems,
    searchQueryUpdateNA: updateNA.searchQuery,
    sortByUpdateNA: updateNA.sortBy,
    isSortDirDescUpdateNA: updateNA.isSortDirDesc,
    filterBranchUpdateNA: updateNA.filterBranch,
    filterAssetGroupUpdateNA: updateNA.filterAssetGroup,
    filterAssetStatusUpdateNA: updateNA.filterAssetStatus,
    startDateFilterUpdateNA: updateNA.startDateFilter,
    endDateFilterUpdateNA: updateNA.endDateFilter,
    dataMetaUpdateNA: updateNA.dataMeta,
    fetchAssetsUpdateNA: updateNA.fetchAssets,

    // ============ Update -> Updated ============
    refAssetsListTableUpdated: updated.refTable,
    currentPageUpdated: updated.currentPage,
    perPageUpdated: updated.perPage,
    totalItemsUpdated: updated.totalItems,
    searchQueryUpdated: updated.searchQuery,
    sortByUpdated: updated.sortBy,
    isSortDirDescUpdated: updated.isSortDirDesc,
    filterBranchUpdated: updated.filterBranch,
    filterAssetGroupUpdated: updated.filterAssetGroup,
    filterAssetStatusUpdated: updated.filterAssetStatus,
    startDateFilterUpdated: updated.startDateFilter,
    endDateFilterUpdated: updated.endDateFilter,
    dataMetaUpdated: updated.dataMeta,
    fetchAssetsUpdated: updated.fetchAssets,

    // ============ Dispose -> On Going ============
    refAssetsListTableOnGoing: disposeOnGoing.refTable,
    currentPageOnGoing: disposeOnGoing.currentPage,
    perPageOnGoing: disposeOnGoing.perPage,
    totalItemsOnGoing: disposeOnGoing.totalItems,
    searchQueryOnGoing: disposeOnGoing.searchQuery,
    sortByOnGoing: disposeOnGoing.sortBy,
    isSortDirDescOnGoing: disposeOnGoing.isSortDirDesc,
    filterBranchOnGoing: disposeOnGoing.filterBranch,
    filterAssetGroupOnGoing: disposeOnGoing.filterAssetGroup,
    filterAssetStatusOnGoing: disposeOnGoing.filterAssetStatus,
    startDateFilterOnGoing: disposeOnGoing.startDateFilter,
    endDateFilterOnGoing: disposeOnGoing.endDateFilter,
    dataMetaOnGoing: disposeOnGoing.dataMeta,
    fetchAssetsOnGoing: disposeOnGoing.fetchAssets,

    // ============ Dispose -> Needs Approval ============
    refAssetsListTableNA: disposeNA.refTable,
    currentPageNA: disposeNA.currentPage,
    perPageNA: disposeNA.perPage,
    totalItemsNA: disposeNA.totalItems,
    searchQueryNA: disposeNA.searchQuery,
    sortByNA: disposeNA.sortBy,
    isSortDirDescNA: disposeNA.isSortDirDesc,
    filterBranchNA: disposeNA.filterBranch,
    filterAssetGroupNA: disposeNA.filterAssetGroup,
    filterAssetStatusNA: disposeNA.filterAssetStatus,
    startDateFilterNA: disposeNA.startDateFilter,
    endDateFilterNA: disposeNA.endDateFilter,
    dataMetaNA: disposeNA.dataMeta,
    fetchAssetsNA: disposeNA.fetchAssets,

    // ============ Dispose -> Disposed ============
    refAssetsListTableDisposed: disposed.refTable,
    currentPageDisposed: disposed.currentPage,
    perPageDisposed: disposed.perPage,
    totalItemsDisposed: disposed.totalItems,
    searchQueryDisposed: disposed.searchQuery,
    sortByDisposed: disposed.sortBy,
    isSortDirDescDisposed: disposed.isSortDirDesc,
    filterBranchDisposed: disposed.filterBranch,
    filterAssetGroupDisposed: disposed.filterAssetGroup,
    filterAssetStatusDisposed: disposed.filterAssetStatus,
    startDateFilterDisposed: disposed.startDateFilter,
    endDateFilterDisposed: disposed.endDateFilter,
    dataMetaDisposed: disposed.dataMeta,
    fetchAssetsDisposed: disposed.fetchAssets,

    // ============ Return -> On Going ============
    refAssetsListTableReturnOnGoing: returnOnGoing.refTable,
    currentPageReturnOnGoing: returnOnGoing.currentPage,
    perPageReturnOnGoing: returnOnGoing.perPage,
    totalItemsReturnOnGoing: returnOnGoing.totalItems,
    searchQueryReturnOnGoing: returnOnGoing.searchQuery,
    sortByReturnOnGoing: returnOnGoing.sortBy,
    isSortDirDescReturnOnGoing: returnOnGoing.isSortDirDesc,
    filterBranchReturnOnGoing: returnOnGoing.filterBranch,
    filterAssetGroupReturnOnGoing: returnOnGoing.filterAssetGroup,
    filterAssetStatusReturnOnGoing: returnOnGoing.filterAssetStatus,
    startDateFilterReturnOnGoing: returnOnGoing.startDateFilter,
    endDateFilterReturnOnGoing: returnOnGoing.endDateFilter,
    dataMetaReturnOnGoing: returnOnGoing.dataMeta,
    fetchAssetsReturnOnGoing: returnOnGoing.fetchAssets,

    // ============ Return -> Needs Approval ============
    refAssetsListTableReturnNA: returnNA.refTable,
    currentPageReturnNA: returnNA.currentPage,
    perPageReturnNA: returnNA.perPage,
    totalItemsReturnNA: returnNA.totalItems,
    searchQueryReturnNA: returnNA.searchQuery,
    sortByReturnNA: returnNA.sortBy,
    isSortDirDescReturnNA: returnNA.isSortDirDesc,
    filterBranchReturnNA: returnNA.filterBranch,
    filterAssetGroupReturnNA: returnNA.filterAssetGroup,
    filterAssetStatusReturnNA: returnNA.filterAssetStatus,
    startDateFilterReturnNA: returnNA.startDateFilter,
    endDateFilterReturnNA: returnNA.endDateFilter,
    dataMetaReturnNA: returnNA.dataMeta,
    fetchAssetsReturnNA: returnNA.fetchAssets,

    // ============ Return -> Returned ============
    refAssetsListTableReturned: returned.refTable,
    currentPageReturned: returned.currentPage,
    perPageReturned: returned.perPage,
    totalItemsReturned: returned.totalItems,
    searchQueryReturned: returned.searchQuery,
    sortByReturned: returned.sortBy,
    isSortDirDescReturned: returned.isSortDirDesc,
    filterBranchReturned: returned.filterBranch,
    filterAssetGroupReturned: returned.filterAssetGroup,
    filterAssetStatusReturned: returned.filterAssetStatus,
    startDateFilterReturned: returned.startDateFilter,
    endDateFilterReturned: returned.endDateFilter,
    dataMetaReturned: returned.dataMeta,
    fetchAssetsReturned: returned.fetchAssets,
  }
}